// @ts-check
import { formatDate, moduleTypeToLabel } from '@/lib/utils';
import {
  REVIEW_CHANGE_BADGE_CONFLICT,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_ADDED,
  REVIEW_CHANGE_BADGE_STUDIO_EDIT,
  REVIEW_CHANGE_BADGE_STUDIO_ADDED,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_ONLY,
  REVIEW_CHANGE_BADGE_STUDIO_ONLY,
  REVIEW_CHANGE_BADGE_STUDIO_REMOVED,
  REVIEW_CHANGE_BADGE_MANUSCRIPT_REMOVED,
  REVIEW_CHANGE_BADGE_CONFIG,
  REVIEW_CHANGE_DECISION_ACCEPT,
  REVIEW_CHANGE_DECISION_REJECT,
  REVIEW_CHANGE_DECISION_KEEP,
  REVIEW_CHANGE_DECISION_APPLY_MANUSCRIPT,
  REVIEW_CHANGE_DECISION_APPLY_STUDIO,
  APPLY_STATUS_APPLIED,
  APPLY_STATUS_REJECTED,
  APPLY_STATUS_STALE,
  APPLY_STATUS_FAILED,
  APPLY_STATUS_UNSUPPORTED,
  APPLY_STATUS_SKIPPED,
  APPLY_STATUS_WOULD_APPLY,
  APPLY_STATUS_WOULD_REJECT,
} from '@/lib/constants/manuscripts/store';
import {
  proseMirrorToHtml,
  hasProseMirrorText,
  flattenParagraphRuns,
  renderMarkedText,
  renderNebulaIcon,
  decodeHtmlEntities,
} from './proseMirrorHtml';

/**
 * @typedef {import('./studioDiffTypes').DriftChange} DriftChange
 * @typedef {import('./studioDiffTypes').DriftResponse} DriftResponse
 * @typedef {import('./studioDiffTypes').DriftSide} DriftSide
 * @typedef {import('./studioDiffTypes').TextDiffParagraph} TextDiffParagraph
 * @typedef {import('./studioDiffTypes').ReviewChangeSlot} ReviewChangeSlot
 * @typedef {import('./studioDiffTypes').ReviewChangeCardItem} ReviewChangeCardItem
 * @typedef {import('./studioDiffTypes').ApplyDecision} ApplyDecision
 * @typedef {import('./studioDiffTypes').ApplyDecisionResult} ApplyDecisionResult
 * @typedef {import('./studioDiffTypes').ApplySummary} ApplySummary
 * @typedef {import('./studioDiffTypes').ApplyOutcome} ApplyOutcome
 */

const CONTENT_NOT_PREVIEWABLE = 'This content type isn\'t previewable yet.';
const EMPTY_LABEL = '<Empty>';

// A badge the backend sends that isn't in this config is dropped rather than guessed at.
const KNOWN_BADGES = new Set(Object.keys(REVIEW_CHANGE_BADGE_CONFIG));

/** @param {DriftSide | null | undefined} side */
function sideTime(side) {
  return side?.updated_on ? formatDate(side.updated_on, true) : null;
}

// manuscript's own Page.number vs. Studio's 1-indexed sort_index rank -- can differ after a reorder.
/** @param {DriftSide | null | undefined} side */
function slideLabel(side) {
  return Number.isFinite(side?.order) ? `Slide ${side.order}` : null;
}

/**
 * @typedef {{ text: string; highlight?: string | null }} ParagraphRun
 */

/** @param {ParagraphRun[][]} paragraphs */
function hasVisibleText(paragraphs) {
  return paragraphs.some((paragraph) => paragraph.some((segment) => segment.text?.trim()));
}

// A whole paragraph added/removed outright has no segments -- synthesize one from old/new.
/** @param {TextDiffParagraph} paragraph */
function paragraphSegments(paragraph) {
  if (paragraph.segments) return paragraph.segments;
  if (paragraph.op === 'insert') return paragraph.new ? [{ op: 'insert', text: paragraph.new }] : [];
  if (paragraph.op === 'delete') return paragraph.old ? [{ op: 'delete', text: paragraph.old }] : [];
  return [
    ...(paragraph.old ? [{ op: 'delete', text: paragraph.old }] : []),
    ...(paragraph.new ? [{ op: 'insert', text: paragraph.new }] : []),
  ];
}

// An 'equal' paragraph has identical old/new text -- either works as the plain text.
/** @param {TextDiffParagraph} paragraph */
function paragraphText(paragraph) {
  return paragraph.new ?? paragraph.old ?? '';
}

// Splits `runs` (from flattenParagraphRuns) at the offsets of this side's own segments ('equal' + `ownOp`).
// A "\n" in segment text maps to the next hardBreak run, so a change next to a break stays on its line;
// with no "\n" at all, breaks are zero-width and flush right after the content before them.
function alignRunsWithSegments(runs, segments, ownOp, highlight) {
  const ownSegments = segments.filter((segment) => segment.op === 'equal' || segment.op === ownOp);
  let runIdx = 0;
  let offsetInRun = 0;
  const takeText = (len) => {
    const pieces = [];
    let remaining = len;
    while (runIdx < runs.length && remaining > 0) {
      const run = runs[runIdx];
      if (run.icon || run.break) {
        pieces.push(run.icon ? { icon: run.icon } : { break: true });
        runIdx += 1;
      } else {
        const take = Math.min(run.text.length - offsetInRun, remaining);
        if (take > 0) pieces.push({ text: run.text.substr(offsetInRun, take), marks: run.marks });
        offsetInRun += take;
        remaining -= take;
        if (offsetInRun >= run.text.length) {
          runIdx += 1;
          offsetInRun = 0;
        }
      }
    }
    return pieces;
  };

  // One "\n" in segment text = the next break run; icons sitting before it go first.
  const takeBreak = () => {
    const pieces = [];
    while (runIdx < runs.length && runs[runIdx].icon) {
      pieces.push({ icon: runs[runIdx].icon });
      runIdx += 1;
    }
    if (runs[runIdx]?.break) {
      pieces.push({ break: true });
      runIdx += 1;
    }
    return pieces;
  };

  const consume = (text) => text.split('\n').flatMap((part, index) => [
    ...(index > 0 ? takeBreak() : []),
    ...takeText(part.length),
  ]);

  // An icon left stranded right after a consume() call belongs to the content that
  // precedes it, not to whichever segment's consume() happens to reach it next.
  const flushIcons = () => {
    const pieces = [];
    while (runIdx < runs.length && runs[runIdx].icon) {
      pieces.push({ icon: runs[runIdx].icon });
      runIdx += 1;
    }
    return pieces;
  };

  const flushTrailing = () => {
    const pieces = [];
    while (runIdx < runs.length && (runs[runIdx].icon || runs[runIdx].break)) {
      pieces.push(runs[runIdx].icon ? { icon: runs[runIdx].icon } : { break: true });
      runIdx += 1;
    }
    return pieces;
  };

  const breaksInText = ownSegments.some((segment) => segment.text?.includes('\n'));
  const flushStranded = breaksInText ? flushIcons : flushTrailing;

  const aligned = ownSegments.flatMap((segment) => {
    // A whitespace-only change has nothing visible to mark, so none of its pieces are highlighted.
    const pieceHighlight = segment.op === ownOp && segment.text.trim() ? highlight : null;
    const consumed = consume(segment.text).map((piece) => ({ ...piece, highlight: pieceHighlight }));
    return [...consumed, ...flushStranded()];
  });
  return [...aligned, ...flushTrailing()];
}

function highlightWrap(html, highlight) {
  if (!highlight) return html;
  return `<span class="review-change-rich-text__highlight review-change-rich-text__highlight--${highlight}">${html}</span>`;
}

function renderAlignedRun(run) {
  if (run.icon) return renderNebulaIcon(run.icon);
  if (run.break) return '<br>';
  return renderMarkedText(run.text, run.marks);
}

// text_diff's insert/delete are always manuscript -> studio, so a 'delete' entry has no
// counterpart in studio's real doc, and an 'insert' entry has no counterpart in manuscript's.
function existsOnSide(paragraph, side) {
  return side === 'studio' ? paragraph.op !== 'delete' : paragraph.op !== 'insert';
}

// The text_diff op whose text exists only in that side's doc.
const OWN_OP = { manuscript: 'delete', studio: 'insert' };

// Git-style split view, keyed by the edited side: each side shows only its own real text,
// its own divergent part highlighted -- the edited side's as added, the other's as removed.
const SPLIT_HIGHLIGHTS = {
  studio: { manuscript: 'removed', studio: 'added' },
  manuscript: { manuscript: 'added', studio: 'removed' },
};

// proseMirrorToHtml `renderParagraph` override: splices this side's text_diff highlighting onto
// paragraph[index]'s real runs (index = doc order, filtered to entries that exist on
// this side -- a raw index would desync past any one-sided paragraph); 'equal' renders as-is.
function buildRenderParagraph(paragraphs, side, highlight) {
  const sideParagraphs = paragraphs.filter((paragraph) => existsOnSide(paragraph, side));
  return (node, index) => {
    const diffEntry = sideParagraphs[index];
    if (!diffEntry || diffEntry.op === 'equal') return null;
    const runs = flattenParagraphRuns(node);
    const segments = paragraphSegments(diffEntry);
    const aligned = alignRunsWithSegments(runs, segments, OWN_OP[side], highlight);
    return aligned
      .map((run) => highlightWrap(renderAlignedRun(run), run.highlight))
      .join('');
  };
}

// A side's raw ProseMirror JSON (studio: options.content; manuscript: text when
// text_type is 'prosemirror'); null otherwise -- callers fall back to flat rendering.
/**
 * @param {DriftChange} item
 * @param {'studio' | 'manuscript'} sideKey
 */
function rawProseMirrorDoc(item, sideKey) {
  const side = item[sideKey];
  if (sideKey === 'studio') return side?.content?.options?.content ?? null;
  return side?.content?.text_type === 'prosemirror' ? (side?.content?.text ?? null) : null;
}

/** @param {string | null} raw */
function parsedProseMirrorDoc(raw) {
  if (!raw) return null;
  try {
    const doc = JSON.parse(raw);
    return hasProseMirrorText(doc, { ignoreWhitespace: true }) ? doc : null;
  } catch {
    return null;
  }
}

// Real doc + this side's text_diff highlighting spliced in; null (no doc/bad JSON/no text) falls back to flat paragraphs.
/**
 * @param {DriftChange} item
 * @param {'studio' | 'manuscript'} side
 * @param {TextDiffParagraph[]} paragraphs
 * @param {string} highlight
 */
function richSideHtml(item, side, paragraphs, highlight) {
  const doc = parsedProseMirrorDoc(rawProseMirrorDoc(item, side));
  if (!doc) return null;
  return proseMirrorToHtml(doc, { renderParagraph: buildRenderParagraph(paragraphs, side, highlight) });
}

// Flat fallback: a paragraph only the other side has renders blank here, but still positioned.
/**
 * @param {TextDiffParagraph[]} paragraphs
 * @param {'studio' | 'manuscript'} side
 * @param {string} highlight
 * @returns {ParagraphRun[][]}
 */
function flatSideParagraphs(paragraphs, side, highlight) {
  const ownOp = OWN_OP[side];
  return paragraphs.map((paragraph) => (paragraph.op === 'equal'
    ? [{ text: paragraphText(paragraph), highlight: null }]
    : paragraphSegments(paragraph)
      .filter((segment) => segment.op === 'equal' || segment.op === ownOp)
      .map((segment) => ({ text: segment.text, highlight: segment.op === ownOp ? highlight : null }))));
}

// Prefer real-doc HTML, then flat paragraphs, then the placeholder.
/**
 * @param {string | null} richHtml
 * @param {ParagraphRun[][]} flatParagraphs
 * @returns {Pick<ReviewChangeSlot, 'html' | 'paragraphs' | 'text' | 'notPreviewable'>}
 */
function contentSlotFields(richHtml, flatParagraphs) {
  if (richHtml) return { html: richHtml };
  if (hasVisibleText(flatParagraphs)) return { paragraphs: flatParagraphs };
  return { text: CONTENT_NOT_PREVIEWABLE, notPreviewable: true };
}

/**
 * @param {DriftSide | null | undefined} side
 * @param {string | null} richHtml
 * @param {ParagraphRun[][]} flatParagraphs
 * @returns {ReviewChangeSlot}
 */
function buildSlot(side, richHtml, flatParagraphs) {
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...contentSlotFields(richHtml, flatParagraphs),
  };
}

// No visible text on this side (e.g. all of it deleted): an empty state, not "isn't previewable".
/**
 * @param {DriftSide | null | undefined} side
 * @returns {ReviewChangeSlot}
 */
function emptySideSlot(side) {
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    text: EMPTY_LABEL,
    isEmpty: true,
  };
}

// Each side renders its own real doc, so a mark-only divergence (e.g. one side highlighted a
// phrase) stays visible. Conflict has no ancestor text, so it reuses studio edit's colors as a
// fixed per-side convention -- red/green there is not true add/remove attribution.
/**
 * @param {DriftChange} item
 * @returns {{ manuscript: ReviewChangeSlot; studio: ReviewChangeSlot }}
 */
function buildDiffedSides(item) {
  const paragraphs = item.text_diff?.paragraphs ?? [];
  const editedSide = item.badge === REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT ? 'manuscript' : 'studio';
  const highlights = SPLIT_HIGHLIGHTS[editedSide];
  /** @param {'studio' | 'manuscript'} side */
  const sideSlot = (side) => {
    const richHtml = richSideHtml(item, side, paragraphs, highlights[side]);
    const flatParagraphs = flatSideParagraphs(paragraphs, side, highlights[side]);
    // An incomplete diff never quotes an unreadable side, so its missing text is not proof of deletion.
    if (!item.text_diff?.incomplete && !richHtml && !hasVisibleText(flatParagraphs)) {
      return emptySideSlot(item[side]);
    }
    return buildSlot(item[side], richHtml, flatParagraphs);
  };
  return { manuscript: sideSlot('manuscript'), studio: sideSlot('studio') };
}

// A link/button carries no text_diff, and its manuscript side may carry no content; link_diff's old (manuscript) -> new (studio) per changed field is the only per-side text.
const LINK_DIFF_FIELDS = ['label', 'target_url'];

/** @param {DriftChange} item */
function linkDiffEntries(item) {
  return LINK_DIFF_FIELDS.map((field) => item.link_diff?.find((entry) => entry.field === field)).filter(Boolean);
}

// Edited on one side = only that side highlighted; conflict = manuscript removed / studio added, like conflict text.
const LINK_DIFF_HIGHLIGHTS = {
  [REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT]: { manuscript: 'added', studio: null },
  [REVIEW_CHANGE_BADGE_STUDIO_EDIT]: { manuscript: null, studio: 'added' },
  [REVIEW_CHANGE_BADGE_CONFLICT]: { manuscript: 'removed', studio: 'added' },
};

/** @param {DriftChange} item */
function hasLinkDiff(item) {
  return linkDiffEntries(item).length > 0 && !!LINK_DIFF_HIGHLIGHTS[item.badge];
}

/** @param {DriftChange} item */
function buildLinkDiffSides(item) {
  const entries = linkDiffEntries(item);
  const highlights = LINK_DIFF_HIGHLIGHTS[item.badge];
  const paragraphsFor = (key, sideHighlight) => entries
    .map((entry) => [{ text: entry[key] || EMPTY_LABEL, highlight: sideHighlight }]);
  return {
    manuscript: buildSlot(item.manuscript, null, paragraphsFor('old', highlights.manuscript)),
    studio: buildSlot(item.studio, null, paragraphsFor('new', highlights.studio)),
  };
}

// Generation turns every link item into an advanced_button, so only a row with a Studio counterpart can be told apart.
/** @param {DriftChange} item */
function isButtonItem(item) {
  return item.content_type === 'link' && item.studio?.content?.type === 'advanced_button';
}

// manuscript side is a curated { target_url, external_url }; Studio side is
// the module's raw options doc, where a URL only exists for options.action === 'link'.
/**
 * @param {DriftSide} side
 * @param {boolean} isStudio
 */
function linkUrl(side, isStudio) {
  if (isStudio) {
    const options = side.content?.options ?? {};
    return options.action === 'link' ? (options.target ?? null) : null;
  }
  return side.content?.target_url || side.content?.external_url || null;
}

const CONTENT_TYPE_LABELS = {
  internal_asset: 'Asset',
  link: 'Link',
};

function humanize(value) {
  if (!value) return '';
  return value.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

// content_type is absent for module types the backend doesn't categorize (e.g. Shape) -- fall back to the raw module type instead of defaulting to Text.
/** @param {DriftChange} item */
function contentTypeLabel(item) {
  if (item.content_type) return CONTENT_TYPE_LABELS[item.content_type] || humanize(item.content_type);
  // A structural (page/window/section) row has no content_type or module content to read a type from.
  if (item.type && item.type !== 'module') return humanize(item.type);
  const rawModuleType = item.studio?.content?.type ?? item.manuscript?.content?.type;
  return moduleTypeToLabel(rawModuleType) || 'Text';
}

/**
 * @param {string} contentType
 * @param {DriftSide | null | undefined} side
 */
function notPreviewableText(contentType, side) {
  const moduleType = moduleTypeToLabel(side?.content?.type);
  const detail = moduleType && moduleType !== contentType ? ` (${moduleType})` : '';
  return `${contentType} content${detail} isn't previewable here yet.`;
}

// These TEI types store options.content as a plain string, not JSON-stringified ProseMirror.
const PLAIN_TEXT_STUDIO_MODULE_TYPES = new Set(['prompt', 'single_selection', 'multiple_selection', 'block']);

// manuscript is a curated { text, text_type }; studio's text (if any) is ProseMirror
// JSON at options.content. Returns { text } for a plain string, { html } for ProseMirror.
/**
 * @param {DriftSide} side
 * @param {boolean} isStudio
 * @returns {Pick<ReviewChangeSlot, 'text' | 'html'> | null}
 */
function richTextFromContent(side, isStudio) {
  const raw = isStudio ? side.content?.options?.content : side.content?.text;
  if (!raw) return null;
  if (!isStudio && side.content?.text_type !== 'prosemirror') return { text: decodeHtmlEntities(raw) };
  if (isStudio && PLAIN_TEXT_STUDIO_MODULE_TYPES.has(side.content?.type)) return { text: raw };
  try {
    const doc = JSON.parse(raw);
    return hasProseMirrorText(doc) ? { html: proseMirrorToHtml(doc) } : null;
  } catch {
    return null;
  }
}

// assetLinkUrl mirrors the "Open Asset" link Studio's own MiniMenu builds.
// manuscript side is a curated { asset_id }; Studio side nests it under options,
// same as an asset module's own moduleDefaults shape (options.asset_id).
/**
 * @param {DriftSide} side
 * @param {boolean} isStudio
 * @param {string | undefined} appDomain
 * @param {string} contentType
 * @returns {ReviewChangeSlot}
 */
function buildNoDiffSide(side, isStudio, appDomain, contentType) {
  const assetId = isStudio ? side.content?.options?.asset_id : side.content?.asset_id;
  const url = linkUrl(side, isStudio);
  const richText = !assetId && !url ? richTextFromContent(side, isStudio) : null;
  // Manuscript has no shape concept -- only the raw Studio module content carries one.
  const shapeModuleData = (!assetId && !url && !richText && isStudio && side.content?.type === 'shape')
    ? side.content : null;
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    ...(assetId && { assetId, ...(appDomain && { assetLinkUrl: `${appDomain}/player/${assetId}` }) }),
    ...(url && { linkUrl: url }),
    ...richText,
    ...(shapeModuleData && { shapeModuleData }),
    ...(!assetId && !url && !richText && !shapeModuleData
      && { text: notPreviewableText(contentType, side), notPreviewable: true }),
  };
}

const SECTION_FIELDS = [
  { key: 'title', label: 'Title', format: (value) => value },
  { key: 'icon', label: 'Icon', format: (value) => value },
  { key: 'duration_minutes', label: 'Duration', format: (value) => `${value} min` },
];

/** @param {DriftChange} item */
function hasSectionFields(item) {
  return item.content_type === 'section' && SECTION_FIELDS.some(({ key }) => (
    item.manuscript?.content?.[key] != null || item.studio?.content?.[key] != null));
}

// Edited in both = 'changed' on both sides; edited in one = 'added' on that side only, the other stays plain.
const SECTION_EDITED_SIDE_BADGES = {
  manuscript: [REVIEW_CHANGE_BADGE_MANUSCRIPT_EDIT, REVIEW_CHANGE_BADGE_MANUSCRIPT_ADDED],
  studio: [REVIEW_CHANGE_BADGE_STUDIO_EDIT, REVIEW_CHANGE_BADGE_STUDIO_ADDED],
};

/**
 * @param {DriftChange} item
 * @param {'manuscript' | 'studio'} sideKey
 */
function sectionHighlight(item, sideKey) {
  if (item.badge === REVIEW_CHANGE_BADGE_CONFLICT) return 'changed';
  return SECTION_EDITED_SIDE_BADGES[sideKey].includes(item.badge) ? 'added' : null;
}

// A field shows when either side has it; it is highlighted only where its value differs from the other side.
/**
 * @param {DriftChange} item
 * @param {'manuscript' | 'studio'} sideKey
 * @returns {ReviewChangeSlot}
 */
function buildSectionSide(item, sideKey) {
  const side = item[sideKey];
  const content = side.content ?? {};
  const otherContent = item[sideKey === 'studio' ? 'manuscript' : 'studio']?.content ?? {};
  const highlight = sectionHighlight(item, sideKey);
  return {
    slideLabel: slideLabel(side),
    time: sideTime(side),
    sectionFields: SECTION_FIELDS
      .filter(({ key }) => content[key] != null || otherContent[key] != null)
      .map(({ key, label, format }) => ({
        label,
        value: content[key] != null ? format(content[key]) : null,
        highlight: content[key] !== otherContent[key] ? highlight : null,
      })),
  };
}

/**
 * @param {DriftChange} item
 * @param {string | undefined} appDomain
 */
function buildAddedSides(item, appDomain) {
  if (hasSectionFields(item)) {
    return {
      manuscript: item.manuscript ? buildSectionSide(item, 'manuscript') : null,
      studio: item.studio ? buildSectionSide(item, 'studio') : null,
    };
  }
  const contentType = contentTypeLabel(item);
  return {
    manuscript: item.manuscript ? buildNoDiffSide(item.manuscript, false, appDomain, contentType) : null,
    studio: item.studio ? buildNoDiffSide(item.studio, true, appDomain, contentType) : null,
  };
}

// One entry per `change` value the backend sends: modify/add/remove/reorder.
const CHANGE_LABELS = {
  modify: 'Modified',
  add: 'Added',
  remove: 'Removed',
  reorder: 'Reordered',
};

// add/remove no longer name the side that acted, but their badge still does. Keyed on
// `change` first because a badge alone can mislead: studio_edit also marks a reorder.
const DIRECTED_CHANGE_LABELS = {
  add: {
    [REVIEW_CHANGE_BADGE_STUDIO_ADDED]: 'Added in Studio',
    [REVIEW_CHANGE_BADGE_STUDIO_ONLY]: 'Added in Studio',
    [REVIEW_CHANGE_BADGE_MANUSCRIPT_ADDED]: 'Added in Manuscript',
    [REVIEW_CHANGE_BADGE_MANUSCRIPT_ONLY]: 'Added in Manuscript',
  },
  remove: {
    [REVIEW_CHANGE_BADGE_STUDIO_REMOVED]: 'Removed from Studio',
    [REVIEW_CHANGE_BADGE_MANUSCRIPT_REMOVED]: 'Removed from Manuscript',
  },
};

/** @param {DriftChange} item */
function changeLabel(item) {
  return DIRECTED_CHANGE_LABELS[item.change]?.[item.badge]
    || CHANGE_LABELS[item.change]
    || humanize(item.change);
}

const CHANNEL_LABELS = {
  teacher: 'Teacher',
  student: 'Student',
};

// "<channel> <content_type>"; rows without a channel (sections, pages) get no prefix.
/** @param {DriftChange} item */
function channelTypeLabel(item) {
  const contentType = contentTypeLabel(item);
  const channel = item.channel ? (CHANNEL_LABELS[item.channel] || humanize(item.channel)) : '';
  return [channel, contentType].filter(Boolean).join(' ');
}

/** @param {DriftChange} item */
function changeTypeLabel(item) {
  return [channelTypeLabel(item), changeLabel(item)].filter(Boolean).join(' ');
}

// A shape's backend label is always "Untitled" -- show its own type instead. Wording matches ShapeOptions.vue's shapeTypeOptions dropdown.
const SHAPE_TYPE_LABELS = {
  circle: 'Circle',
  box: 'Square',
  line: 'Line',
  arrow: 'Arrow',
  pentagon: 'Pentagon',
  rounded_rectangle: 'Rounded Rectangle',
  speech_bubble_lower_left: 'Speech Bubble Lower Left',
  speech_bubble_lower_right: 'Speech Bubble Lower Right',
  title_bubble: 'Title Bubble',
};

/** @param {string | null | undefined} rawShapeType */
function shapeTypeLabel(rawShapeType) {
  if (!rawShapeType) return 'Rectangle (Default)';
  return SHAPE_TYPE_LABELS[rawShapeType] || humanize(rawShapeType);
}

// No asset title anywhere in this response -- fetching one needs its own API call, so fall back to the id.
/** @param {DriftChange} item */
function itemAssetId(item) {
  return item.studio?.content?.options?.asset_id ?? item.manuscript?.content?.asset_id ?? null;
}

/** @param {DriftChange} item */
function itemLabel(item) {
  if (item.studio?.content?.type === 'shape') return shapeTypeLabel(item.studio.content.options?.shape);
  if (item.content_type === 'internal_asset' && (!item.label || item.label === 'Untitled')) {
    return itemAssetId(item) || item.label;
  }
  return item.label;
}

/** @param {DriftChange} item */
function cardTitle(item) {
  const primary = item.section?.title || itemLabel(item) || 'Untitled';
  const changeType = changeTypeLabel(item);
  const showLabel = !!(item.section?.title && item.label) && item.type !== 'section';
  const parts = [primary];
  if (changeType) parts.push(showLabel ? `${changeType}: ${itemLabel(item)}` : changeType);
  return parts.join(' | ');
}

/** @param {DriftChange} item */
function navLabel(item) {
  const primary = item.section?.title || itemLabel(item) || 'Untitled';
  const changeType = changeTypeLabel(item);
  const parts = [primary];
  if (changeType) parts.push(changeType);
  if (item.page?.order != null) parts.push(`Page: ${item.page.order}`);
  return parts.join(' | ');
}

// A row's own badge already names the situation ("Manuscript only") for the
// *_only case; can_sync:false on an otherwise-decidable badge needs a suffix
// since the plain badge text alone doesn't explain the gate. No new backend
// field -- both derive from `badge`/`can_sync`, which the endpoint already sends.
/** @param {{ badge?: string }} item */
function nonDecidableReason(item) {
  const badgeText = REVIEW_CHANGE_BADGE_CONFIG[item.badge]?.text ?? '';
  if (!REVIEW_CHANGE_BADGE_CONFIG[item.badge]?.decidable) return badgeText;
  return `${badgeText} — can't sync yet`;
}

/** @param {DriftChange} item */
function isDecidable(item) {
  return !!item.can_sync && !!REVIEW_CHANGE_BADGE_CONFIG[item.badge]?.decidable;
}

// Only these two notice badges surface in the panel, read-only. Other notices (headers, slot rows) stay hidden.
const SURFACED_NOTICE_BADGES = new Set([REVIEW_CHANGE_BADGE_STUDIO_ONLY, REVIEW_CHANGE_BADGE_MANUSCRIPT_ONLY]);

/** @param {DriftChange} item */
function isPlaceholder(item) {
  return item.content_type === 'placeholder'
    || item.studio?.content?.type === 'placeholder'
    || item.manuscript?.content?.type === 'placeholder';
}

/** @param {DriftChange} item */
function choiceTexts(item) {
  if (!item.choices?.length) return null;
  const textFor = (direction) => item.choices.find((choice) => choice.direction === direction)?.consequence_text;
  return { applyStudio: textFor('to_manuscript'), applyManuscript: textFor('to_studio') };
}

/** @param {{ manuscript?: ReviewChangeSlot | null; studio?: ReviewChangeSlot | null }} sides */
function markButtonSides(sides) {
  return {
    manuscript: sides.manuscript && { ...sides.manuscript, isButton: true },
    studio: sides.studio && { ...sides.studio, isButton: true },
  };
}

/**
 * @param {DriftChange} item
 * @param {string | undefined} appDomain
 * @returns {ReviewChangeCardItem}
 */
function mapItem(item, appDomain) {
  let sides;
  if (item.text_diff) sides = buildDiffedSides(item);
  else if (hasLinkDiff(item)) sides = buildLinkDiffSides(item);
  else sides = buildAddedSides(item, appDomain);
  // A section row has no selective apply path on the BE (no_apply_path), so it is read-only.
  const decidable = item.type !== 'section' && (item.badge === REVIEW_CHANGE_BADGE_CONFLICT || isDecidable(item));
  return {
    id: item.id,
    elementType: item.type,
    direction: item.direction,
    manuscriptContentHash: item.manuscript_content_hash,
    studioContentHash: item.studio_content_hash,
    title: cardTitle(item),
    navLabel: navLabel(item),
    badge: item.badge,
    decidable,
    destructive: !!item.destructive,
    ...(item.consequence_text && { consequenceText: item.consequence_text }),
    ...(choiceTexts(item) && { choiceTexts: choiceTexts(item) }),
    ...(decidable ? {} : { reason: nonDecidableReason(item) }),
    ...(isButtonItem(item) ? markButtonSides(sides) : sides),
  };
}

/**
 * Maps the live GET /manuscripts/<id>/studio-diff response into what
 * ReviewChangeCard expects. Only a badge the panel recognizes is required to
 * keep a row -- a non-actionable row (can_sync: false, or a badge like
 * manuscript_only/studio_only that never gets a decide bar) is still kept and
 * rendered read-only via `decidable: false` + `reason`, not dropped. Rows in
 * `notices[]` are appended only for studio_only/manuscript_only, never for
 * placeholders.
 *
 * @param {DriftResponse | null | undefined} diff - the studio-diff response body.
 * @param {string} [appDomain] - `domains.appDomain` from the store, used to
 *   build an internal_asset's "Open Asset" link. Omit to skip that link.
 * @returns {ReviewChangeCardItem[]}
 */
export function mapStudioDiffToReviewChanges(diff, appDomain) {
  const changes = (diff?.changes ?? []).filter((item) => KNOWN_BADGES.has(item.badge));
  const seen = new Set(changes.map((item) => item.id));
  const notices = (diff?.notices ?? []).filter((item) => SURFACED_NOTICE_BADGES.has(item.badge)
    && !isPlaceholder(item) && !seen.has(item.id));
  return [...changes, ...notices].map((item) => mapItem(item, appDomain));
}

/**
 * Panel decisions -> apply request `decisions`; apply-studio is to_manuscript, apply-manuscript is to_studio.
 *
 * @param {ReviewChangeCardItem[]} changes
 * @param {Record<string, string>} decisions - panel decisions keyed by row id.
 * @returns {ApplyDecision[]}
 */
export function buildApplyDecisions(changes, decisions) {
  const applyDecisions = [];
  changes.forEach((item) => {
    const choice = decisions[item.id];
    if (!choice || !item.decidable || !item.elementType) return;
    if (item.blockedDecisions?.includes(choice)) return;

    const isConflict = item.badge === REVIEW_CHANGE_BADGE_CONFLICT;
    let decision;
    let { direction } = item;
    if (isConflict) {
      if (choice === REVIEW_CHANGE_DECISION_APPLY_STUDIO) {
        decision = 'accept';
        direction = 'to_manuscript';
      } else if (choice === REVIEW_CHANGE_DECISION_APPLY_MANUSCRIPT) {
        decision = 'accept';
        direction = 'to_studio';
      } else if (choice === REVIEW_CHANGE_DECISION_KEEP) {
        decision = 'reject';
        direction = 'unchosen';
      }
    } else if (choice === REVIEW_CHANGE_DECISION_ACCEPT) {
      decision = 'accept';
    } else if (choice === REVIEW_CHANGE_DECISION_REJECT) {
      decision = 'reject';
    }
    if (!decision || !direction) return;

    applyDecisions.push({
      element_id: item.id,
      element_type: item.elementType,
      badge: item.badge,
      direction,
      decision,
      manuscript_content_hash: item.manuscriptContentHash ?? null,
      studio_content_hash: item.studioContentHash ?? null,
    });
  });
  return applyDecisions;
}

/**
 * Apply response -> counts; `other` is failed, skipped or unsupported.
 *
 * @param {ApplyDecisionResult[]} results
 * @returns {ApplySummary}
 */
export function summarizeApplyResults(results) {
  const summary = {
    total: results.length, applied: 0, rejected: 0, stale: 0, other: 0, wouldGo: 0, problemId: undefined,
  };
  results.forEach((result) => {
    if (result.status === APPLY_STATUS_APPLIED) summary.applied += 1;
    else if (result.status === APPLY_STATUS_REJECTED) summary.rejected += 1;
    else if (result.status === APPLY_STATUS_WOULD_APPLY || result.status === APPLY_STATUS_WOULD_REJECT) summary.wouldGo += 1;
    else if (result.status === APPLY_STATUS_STALE) summary.stale += 1;
    else summary.other += 1;
    if (result.status === APPLY_STATUS_FAILED && !summary.problemId) summary.problemId = result.problem_id;
  });
  return summary;
}

// BE refusals that hold for every decision on the row, not just the one that was sent.
const ROW_LEVEL_REFUSALS = new Set(['no_apply_path', 'removal_unavailable']);

/** @param {{ badge: string }} row */
function availableDecisions(row) {
  return row.badge === REVIEW_CHANGE_BADGE_CONFLICT
    ? [REVIEW_CHANGE_DECISION_APPLY_STUDIO, REVIEW_CHANGE_DECISION_APPLY_MANUSCRIPT, REVIEW_CHANGE_DECISION_KEEP]
    : [REVIEW_CHANGE_DECISION_ACCEPT, REVIEW_CHANGE_DECISION_REJECT];
}

/**
 * The panel decision a refused apply result was sent for.
 *
 * @param {ApplyDecisionResult} result
 * @param {{ badge: string }} row
 */
function refusedDecision(result, row) {
  if (row.badge === REVIEW_CHANGE_BADGE_CONFLICT) {
    if (result.decision === 'reject') return REVIEW_CHANGE_DECISION_KEEP;
    return result.direction === 'to_manuscript'
      ? REVIEW_CHANGE_DECISION_APPLY_STUDIO
      : REVIEW_CHANGE_DECISION_APPLY_MANUSCRIPT;
  }
  return result.decision === 'reject' ? REVIEW_CHANGE_DECISION_REJECT : REVIEW_CHANGE_DECISION_ACCEPT;
}

/** @param {ApplyDecisionResult} result */
function isRefused(result) {
  return result.status === APPLY_STATUS_UNSUPPORTED || result.status === APPLY_STATUS_SKIPPED;
}

/**
 * Panel decisions the BE refused, by row id (row-level refusals list every decision).
 *
 * @param {ApplyDecisionResult[]} results
 * @param {ReviewChangeCardItem[]} changes - the rows the decisions were made against.
 * @returns {Record<string, string[]>}
 */
export function collectRefusedDecisions(results, changes) {
  const rows = new Map(changes.map((item) => [item.id, item]));
  /** @type {Record<string, string[]>} */
  const refused = {};
  results.filter(isRefused).forEach((result) => {
    const row = rows.get(result.element_id);
    if (!row) return;
    const decisions = ROW_LEVEL_REFUSALS.has(result.reason) ? availableDecisions(row) : [refusedDecision(result, row)];
    refused[row.id] = [...new Set([...(refused[row.id] ?? []), ...decisions])];
  });
  return refused;
}

/**
 * Rows refused as unsupported or skipped, keyed by id with the content they were refused at
 * and the decisions that were refused.
 *
 * @param {ApplyDecisionResult[]} results
 * @param {ReviewChangeCardItem[]} changes - the rows the decisions were made against.
 * @returns {Record<string, { badge: string; manuscriptContentHash: string | null; studioContentHash: string | null; decisions: string[] }>}
 */
export function collectBlockedRows(results, changes) {
  const rows = new Map(changes.map((item) => [item.id, item]));
  /** @type {ReturnType<typeof collectBlockedRows>} */
  const blocked = {};
  Object.entries(collectRefusedDecisions(results, changes)).forEach(([id, decisions]) => {
    const row = rows.get(id);
    blocked[id] = {
      badge: row.badge,
      manuscriptContentHash: row.manuscriptContentHash ?? null,
      studioContentHash: row.studioContentHash ?? null,
      decisions,
    };
  });
  return blocked;
}

/** @param {ReviewChangeCardItem} item @param {{ badge: string; manuscriptContentHash: string | null; studioContentHash: string | null } | undefined} entry */
function isSameContent(item, entry) {
  return !!entry
    && entry.badge === item.badge
    && entry.manuscriptContentHash === (item.manuscriptContentHash ?? null)
    && entry.studioContentHash === (item.studioContentHash ?? null);
}

/**
 * Adds newly refused decisions to the ones already remembered while the row's content is unchanged.
 *
 * @param {ReturnType<typeof collectBlockedRows> | undefined} previous
 * @param {ReturnType<typeof collectBlockedRows>} next
 */
export function mergeBlockedRows(previous = {}, next = {}) {
  const merged = { ...previous };
  Object.entries(next).forEach(([id, entry]) => {
    const before = previous[id];
    const same = before && before.badge === entry.badge
      && before.manuscriptContentHash === entry.manuscriptContentHash
      && before.studioContentHash === entry.studioContentHash;
    merged[id] = same ? { ...entry, decisions: [...new Set([...before.decisions, ...entry.decisions])] } : entry;
  });
  return merged;
}

/**
 * Hides refused decisions on a row, or makes it read-only once none is left, while its badge and hashes are unchanged.
 *
 * @param {ReviewChangeCardItem[]} items
 * @param {ReturnType<typeof collectBlockedRows> | undefined} blocked
 * @returns {ReviewChangeCardItem[]}
 */
export function applyBlockedRows(items, blocked) {
  if (!blocked) return items;
  return items.map((item) => {
    const entry = blocked[item.id];
    if (!isSameContent(item, entry)) return item;
    if (availableDecisions(item).every((decision) => entry.decisions.includes(decision))) {
      return { ...item, decidable: false, reason: nonDecidableReason(item) };
    }
    return { ...item, blockedDecisions: entry.decisions };
  });
}

const OUTCOME_BADGES = {
  [APPLY_STATUS_APPLIED]: { label: 'Applied', color: 'green' },
  [APPLY_STATUS_REJECTED]: { label: 'Rejected', color: 'gray' },
  [APPLY_STATUS_WOULD_APPLY]: { label: 'Will apply', color: 'green' },
  [APPLY_STATUS_WOULD_REJECT]: { label: 'Will reject', color: 'gray' },
  [APPLY_STATUS_STALE]: { label: 'Changed', color: 'orange' },
  [APPLY_STATUS_FAILED]: { label: 'Failed', color: 'red' },
  [APPLY_STATUS_SKIPPED]: { label: 'Skipped', color: 'red' },
  [APPLY_STATUS_UNSUPPORTED]: { label: 'Not supported', color: 'red' },
};

/** @param {string | undefined} code */
const humanizeCode = (code) => (code ?? '').replace(/_/g, ' ');

/** @param {ApplyOutcome | undefined} outcome - a real apply that applied or rejected the row. */
export function isOutcomeLocked(outcome) {
  return !!outcome && !outcome.preview
    && (outcome.status === APPLY_STATUS_APPLIED || outcome.status === APPLY_STATUS_REJECTED);
}

/**
 * Apply response -> outcomes by row id.
 *
 * @param {ApplyDecisionResult[]} results
 * @param {boolean} preview
 * @returns {Record<string, ApplyOutcome>}
 */
export function buildApplyOutcomes(results, preview) {
  return Object.fromEntries(results.map((result) => [result.element_id, {
    status: result.status, reason: result.reason, removedAction: result.removed_action, preview,
  }]));
}

/** @param {ApplyOutcome} outcome */
export function describeOutcome(outcome) {
  const badge = OUTCOME_BADGES[outcome.status] ?? { label: humanizeCode(outcome.status), color: 'gray' };
  const detail = humanizeCode(outcome.removedAction ?? outcome.reason);
  return {
    label: detail ? `${badge.label}: ${detail}` : badge.label, color: badge.color, locked: isOutcomeLocked(outcome), preview: outcome.preview,
  };
}

/**
 * The list after an apply: rows the BE no longer returns but that were applied or rejected stay,
 * and rows that did not go through come first.
 *
 * @param {ReviewChangeCardItem[]} before
 * @param {ReviewChangeCardItem[]} after
 * @param {Record<string, ApplyOutcome>} outcomes
 * @returns {ReviewChangeCardItem[]}
 */
export function mergeApplyOutcomes(before, after, outcomes) {
  const fresh = new Set(after.map((item) => item.id));
  const kept = before.filter((item) => !fresh.has(item.id) && isOutcomeLocked(outcomes[item.id]));
  const position = new Map(before.map((item, index) => [item.id, index]));
  const rank = (item) => (outcomes[item.id] && !isOutcomeLocked(outcomes[item.id]) ? 0 : 1);
  return [...after, ...kept].sort((a, b) => rank(a) - rank(b)
    || (position.get(a.id) ?? before.length) - (position.get(b.id) ?? before.length));
}

/** @param {number} count */
function changeNoun(count) {
  return count === 1 ? 'change' : 'changes';
}

/**
 * The apply toast: success, or a sticky warning when some rows did not go through.
 *
 * @param {ApplySummary} summary
 */
export function buildApplyToast(summary) {
  const {
    total, applied, rejected, stale, other, problemId, reason,
  } = summary;
  const notApplied = stale + other;
  const done = [];
  if (applied) done.push(`<strong>${applied}</strong> applied`);
  if (rejected) done.push(`<strong>${rejected}</strong> left as is`);

  if (!notApplied && !reason) {
    return {
      type: 'success',
      header: 'Success',
      lines: [`${done.join(', ')}.`],
      icon: 'check-circle',
      setFadeOutTimerTo: 3000,
    };
  }

  const lines = [];
  if (notApplied) lines.push(`<strong>${notApplied}</strong> of <strong>${total}</strong> ${changeNoun(total)} couldn't be applied.`);
  if (done.length) lines.push(`${done.join(', ')}.`);
  if (reason) lines.push(`Reason: ${humanizeCode(reason)}.`);
  else if (stale) lines.push(`${stale} changed since you reviewed ${stale === 1 ? 'it' : 'them'}. Review again below.`);
  return {
    type: 'warning',
    header: "Some changes couldn't be applied",
    lines,
    icon: 'information',
    disableFadeout: true,
    ...(problemId && { problemId, copyToClipboard: true }),
  };
}

/**
 * The preview toast: only when some rows cannot go through, otherwise the badges say enough.
 *
 * @param {ApplySummary} summary
 */
export function buildPreviewToast(summary) {
  const {
    total, wouldGo, stale, other, reason,
  } = summary;
  const refused = stale + other;
  if (!refused && !reason) return null;

  const lines = [];
  if (refused) lines.push(`<strong>${refused}</strong> of <strong>${total}</strong> ${changeNoun(total)} can't be synced.`);
  if (wouldGo) lines.push(`<strong>${wouldGo}</strong> can be applied.`);
  if (reason) lines.push(`Reason: ${humanizeCode(reason)}.`);
  else lines.push('See the rows below for details.');
  return {
    type: 'warning',
    header: "Some changes can't be synced",
    lines,
    icon: 'information',
    disableFadeout: true,
  };
}

export default mapStudioDiffToReviewChanges;
